import type { Vendor, PropertyVendor } from '../types/providerSearch';
import { apiPost } from './apiClient';
import { loadConfig } from '../envconfig';
import { ERROR_MESSAGES } from '../constants';
import { getPropertyVendorSpecialtyOptions } from '../config/updateProviderConfig';

// Provider Vendor Type Codes
const PROVIDER_VENDOR_TYPE = {
  AUTO: 11,
  PROPERTY: 60
} as const;

// Flat API response format (what the backend sends)
export interface FlatVendorResponse {
  id: string;
  vendorId?: string;
  vendorName: string;
  doingBusinessAs?: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  approximateMiles: number | null;
  specialty: string | null;
  contractType: string | null;
  contractOwner: string | null;
  approvalStatus: string | null;
  notes: string | null;
  providerStatusCd: string;
  comments: string | null;
  contactEmail: string | null;
  rateAgreementUrl?: string;
}

// Define response types
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message: string;
}

// Paginated response type for Type search
export interface PaginatedApiResponse<T> extends ApiResponse<T> {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  startRecord: number;
  endRecord: number;
}

const transformFlatToNested = (flat: FlatVendorResponse, vendorType: 'auto' | 'property' = 'auto'): Vendor | PropertyVendor => {
  // Use contact fields from contacts[0] if available, else fallback to top-level fields
  let phone = flat.phone;
  let contactEmail = flat.contactEmail || '';
  let address = flat.address;
  let city = flat.city;
  let state = flat.state;
  let zip = flat.zip;
  let contacts = [];
  if (Array.isArray((flat as any).contacts) && (flat as any).contacts.length > 0) {
    contacts = (flat as any).contacts;
    const firstContact = contacts[0];
    if (firstContact.contactTelephoneNbr) phone = firstContact.contactTelephoneNbr;
    if (firstContact.contactEmail) contactEmail = firstContact.contactEmail;
    if (firstContact.address) address = firstContact.address;
    if (firstContact.city) city = firstContact.city;
    if (firstContact.state) state = firstContact.state;
    if (firstContact.zip) zip = firstContact.zip;
  }
  const base: any = {
    id: flat.id || flat.vendorName,
    name: flat.doingBusinessAs,
    vendorId: flat.vendorId || flat.id || flat.vendorName,
    address: {
      street: address,
      city: city,
      state: state,
      zipCode: zip
    },
    phone,
    contacts,
    providerContactEmail: contactEmail,
    // Coordinates not used with real API - distance is calculated server-side
    // coordinates: { lat: 0, lon: 0 },
    services: [],
    status: (flat.providerStatusCd as any),
    isSelected: false
  };

  // Add distance if present (provided by API)
  if (flat.approximateMiles !== undefined && flat.approximateMiles !== null) {
    base.approximateMiles = flat.approximateMiles;
  }

  // Add property-specific fields (also used for propertyClaim)
  if (vendorType === 'property') {
    // Map dba to doingBusinessAs if present, else vendorName
    base.dba = (flat as any).doingBusinessAs || '';
    base.taxIdTin = 0;
    base.providerId = '';
    base.adminComments = flat.comments ? [flat.comments] : [];
    // Normalize approvalStatus: API may return array of codes
    if (Array.isArray((flat as any).approvalStatus)) {
      base.approvalStatus = ((flat as any).approvalStatus as string[])
        .map(String)
        .map(s => s.trim())
        .filter(s => s.length > 0)
        .join(',');
    } else {
      base.approvalStatus = flat.approvalStatus || '';
    }
    base.key = false;
    base.notes = flat.notes || '';
    base.yearsInBusiness = 0;
    base.principalOwner = '';
    base.franchiseId = '';
    base.mailingAddress = { street: '', city: '', state: '', zipCode: '' };
    base.providerContactName = '';
    base.providerContactEmail = contactEmail;
    base.localLicense = '';
    base.nationwide = '';
    base.status = flat.providerStatusCd || '';
    base.twentyFourHourResponse = '';
    base.preferredProviderStatus = '';
    // Store specialty codes in serviceCodes field (used by UI for display)
    base.serviceCodes = flat.specialty || '';
    base.providerId = flat.id || ''
  }

  // Add auto-specific fields
  if (vendorType === 'auto') {
    base.providerId = '';
    base.specialityCodeSVC1 = flat.specialty || '';
    base.taxIdTin = 0;
    base.tiesId = 0;
    base.appraisalSoftware1 = '';
    base.appraisalSoftware2 = '';
    base.suppressionIndicator = '';
    base.vendorSpecializations = flat.specialty || '';
    base.providerContactEmail = contactEmail;
    // Map dba for auto as well
    base.dba = (flat as any).doingBusinessAs  || '';
  }

  return base;
};

export const api = {
  // Fetch vendors with optional filters and category (auto or contract)
  fetchVendors: async (
    filters: Record<string, any> = {},
    currentSearchCategory: 'auto' | 'property' = 'auto'
  ): Promise<ApiResponse<(Vendor | PropertyVendor)[]>> => {
    try {
      const queryParams = new URLSearchParams();

      // Add filters to query params
      if (filters.vendorName) queryParams.append('vendorName', filters.vendorName);
      if (filters.vendorId) queryParams.append('vendorId', filters.vendorId);
      if (filters.city) queryParams.append('city', filters.city);
      if (filters.state) queryParams.append('state', filters.state);
      if (filters.zip) queryParams.append('zip', filters.zip);
      if (filters.searchDistance) queryParams.append('searchDistance', filters.searchDistance);
      if (currentSearchCategory) queryParams.append('category', currentSearchCategory);

      const endpoint = `/vendors?${queryParams.toString()}`;
      const response = await apiPost<ApiResponse<(Vendor | PropertyVendor)[]>>(endpoint);

      return response;
    } catch (error) {
      console.error('❌ [FETCH VENDORS] Detailed error:', {
        error,
        filters,
        category: currentSearchCategory,
        timestamp: new Date().toISOString()
      });

      // Extract error message
      const errorMsg = error instanceof Error ? error.message : String(error);

      const userMessage = errorMsg.includes('Network') || errorMsg.toLowerCase().includes('network')
        ? ERROR_MESSAGES.NETWORK_ERROR
        : errorMsg.includes('Status: 401') || errorMsg.includes('401')
          ? ERROR_MESSAGES.SESSION_EXPIRED
          : errorMsg.includes('Status: 403') || errorMsg.includes('403')
            ? ERROR_MESSAGES.PERMISSION_DENIED
            : errorMsg.includes('Status: 500') || errorMsg.includes('500')
              ? ERROR_MESSAGES.REFERENCE_CODE_ERROR
              : errorMsg.includes('Status: 503') || errorMsg.includes('503')
                ? ERROR_MESSAGES.SERVICE_UNAVAILABLE
                : ERROR_MESSAGES.GENERIC_LOAD_ERROR;

      throw new Error(userMessage);
    }
  },

  searchVendors: async (searchCriteria: Record<string, any>, isExternalUser: boolean): Promise<ApiResponse<(Vendor | PropertyVendor)[]> | PaginatedApiResponse<(Vendor | PropertyVendor)[]>> => {
    try {
      // Map category to providerVendorType
      const category = searchCriteria.currentSearchCategory || 'auto';
      let providerVendorType: number;
      if (category === 'auto') {
        providerVendorType = PROVIDER_VENDOR_TYPE.AUTO;
      } else {
        providerVendorType = PROVIDER_VENDOR_TYPE.PROPERTY; // default
      }

      // Build payload based on user type
      let apiEndpoint: string = '';
      let apiPayload: Record<string, any> = {};

      if (isExternalUser) {
        const searchType = searchCriteria.searchType || 'Local';
        const capitalizedSearchType = searchType.charAt(0).toUpperCase() + searchType.slice(1);
        const MAX_PAGE_SIZE = 50;
        const requestedPageSize = parseInt(searchCriteria.pageSize || '10', 10);
        const safePageSize = Math.min(requestedPageSize, MAX_PAGE_SIZE); 

        apiPayload = {
          page: parseInt(searchCriteria.page || '1', 10),
          pageSize: safePageSize,
          providerVendorType: providerVendorType,
          searchType: capitalizedSearchType,
        };

        // Add optional fields - exclude vendorName for Local search
        if (searchCriteria.vendorName && capitalizedSearchType !== 'Local') {
          apiPayload.vendorName = searchCriteria.vendorName;
        }

        // Location fields
        if (searchCriteria.city) apiPayload.city = searchCriteria.city;
        if (searchCriteria.state) apiPayload.state = searchCriteria.state;
        if (searchCriteria.county) apiPayload.county = searchCriteria.county;
        if (searchCriteria.zip) apiPayload.zip = searchCriteria.zip;
        if (searchCriteria.address) apiPayload.address = searchCriteria.address;

        // Search parameters
        if (searchCriteria.searchDistance) {
          apiPayload.searchDistance = parseInt(searchCriteria.searchDistance, 10);
        }
        // Ensure vendorSpecialtyTypeCode is an array of strings
        if (searchCriteria.vendorSpecialtyTypeCode) {
          const v = searchCriteria.vendorSpecialtyTypeCode;

          if (Array.isArray(v)) {
            apiPayload.vendorSpecialtyTypeCode = v
              .map(String)
              .map(code => code.trim())
              .filter(code => code.length > 0);
          } else if (typeof v === 'string') {
            // Support comma-separated string: "600,630"
            apiPayload.vendorSpecialtyTypeCode = v
              .split(',')
              .map(code => code.trim())
              .filter(code => code.length > 0);
          } else {
            apiPayload.vendorSpecialtyTypeCode = [String(v).trim()];
          }
        }

        // Filter fields
        // Ensure approvalStatus is an array of strings
        if (searchCriteria.approvalStatus) {
          const a = searchCriteria.approvalStatus as any;
          if (Array.isArray(a)) {
            apiPayload.approvalStatus = a
              .map(String)
              .map(s => s.trim())
              .filter(s => s.length > 0);
          } else if (typeof a === 'string') {
            apiPayload.approvalStatus = a
              .split(',')
              .map(s => s.trim())
              .filter(s => s.length > 0);
          } else {
            apiPayload.approvalStatus = [String(a).trim()].filter(s => s.length > 0);
          }
        }
        if (searchCriteria.notes) apiPayload.notes = searchCriteria.notes;
        if (searchCriteria.vendorType) apiPayload.vendorType = searchCriteria.vendorType;
        if (searchCriteria.networkSelection) apiPayload['network selection'] = searchCriteria.networkSelection;

        // Property Name Search has no specialty dropdown, but must always include the same 4 specialties.
        // Property Local Search should default to those 4 when user didn't pick any.
        const shouldApplyPropertySpecialties =
          category === 'property' &&
          (searchCriteria.searchType === 'name' ||
            (searchCriteria.searchType === 'local' &&
              (!apiPayload.vendorSpecialtyTypeCode ||
                (Array.isArray(apiPayload.vendorSpecialtyTypeCode) && apiPayload.vendorSpecialtyTypeCode.length === 0))));

        if (shouldApplyPropertySpecialties) {
          try {
            const specialtyOptions = await getPropertyVendorSpecialtyOptions();
            const specialityCodes = specialtyOptions
              .map(opt => String(opt.id).trim())
              .filter(code => code.length > 0);

            if (specialityCodes.length > 0) {
              // For Name search: this forces the 4 specialties.
              // For Local search: this sets defaults only when empty.
              apiPayload.vendorSpecialtyTypeCode = specialityCodes;
            }
          } catch (e) {
            // Non-fatal: if reference-codes fetch fails, proceed without forcing/defaulting.
            console.warn('[SEARCH] Failed to load Property specialty defaults:', e);
          }
        }
      }

      try {
        const { API_URL } = await loadConfig();
        apiEndpoint = `${API_URL}/Providers/search`;
        const responseData = await apiPost<any>(apiEndpoint, apiPayload);

        // Handle response - backend always wraps data in { data: [...], currentPage, pageSize, etc. }
        const data: FlatVendorResponse[] = responseData.data || [];

        // Transform flat API response to nested app structure
        const transformedVendors = data.map(flatVendor =>
          transformFlatToNested(flatVendor, category as 'auto' | 'property')
        );

        // Return with pagination metadata if available
        if (responseData.totalCount !== undefined) {
          const paginatedResponse = {
            success: true,
            data: transformedVendors,
            message: `Found ${responseData.totalCount} vendors (showing page ${responseData.currentPage || 1})`,
            currentPage: responseData.currentPage || 1,
            pageSize: responseData.pageSize || data.length,
            totalCount: responseData.totalCount,
            totalPages: responseData.totalPages || Math.ceil(responseData.totalCount / (responseData.pageSize || data.length)),
            hasPreviousPage: responseData.hasPreviousPage || false,
            hasNextPage: responseData.hasNextPage || false,
            startRecord: responseData.startRecord || 1,
            endRecord: responseData.endRecord || data.length
          } as PaginatedApiResponse<(Vendor | PropertyVendor)[]>;
          return paginatedResponse;
        }

        const simpleResponse = {
          success: true,
          data: transformedVendors,
          message: `Found ${transformedVendors.length} vendors`
        };

        return simpleResponse;
      } catch (error) {
        console.error('❌ [SEARCH VENDORS] Detailed error:', {
          error,
          searchCriteria,
          isExternalUser,
          endpoint: apiEndpoint,
          payload: apiPayload,
          timestamp: new Date().toISOString()
        });

        // Extract error message
        const errorMsg = error instanceof Error ? error.message : String(error);

        // Check for status codes in error message or network errors
        const userMessage = errorMsg.includes('Network') || errorMsg.toLowerCase().includes('network')
          ? ERROR_MESSAGES.NETWORK_ERROR
          : errorMsg.includes('Status: 400') || errorMsg.includes('400')
            ? ERROR_MESSAGES.BAD_REQUEST
            : errorMsg.includes('Status: 401') || errorMsg.includes('401')
              ? ERROR_MESSAGES.SESSION_EXPIRED
              : errorMsg.includes('Status: 403') || errorMsg.includes('403')
                ? ERROR_MESSAGES.PERMISSION_DENIED_SEARCH
                : errorMsg.includes('Status: 404') || errorMsg.includes('404')
                  ? ERROR_MESSAGES.NOT_FOUND
                  : errorMsg.includes('Status: 500') || errorMsg.includes('500')
                    ? ERROR_MESSAGES.REFERENCE_CODE_ERROR
                    : errorMsg.includes('Status: 503') || errorMsg.includes('503')
                      ? ERROR_MESSAGES.SERVICE_UNAVAILABLE_DETAIL
                      : errorMsg.toLowerCase().includes('timeout')
                        ? ERROR_MESSAGES.TIMEOUT_ERROR
                        : ERROR_MESSAGES.GENERIC_SEARCH_ERROR;
        return {
          success: false,
          data: [],
          message: userMessage
        };
      }
    } catch (outerError) {
      console.error('❌ [SEARCH VENDORS] Outer error:', {
        error: outerError,
        timestamp: new Date().toISOString()
      });
      
      // Extract error message
      const errorMsg = outerError instanceof Error ? outerError.message : String(outerError);
      
      // Check for status codes in error message
      const userMessage = errorMsg.includes('Status: 500') || errorMsg.includes('500')
        ? ERROR_MESSAGES.REFERENCE_CODE_ERROR
        : ERROR_MESSAGES.UNEXPECTED_ERROR;
      
      return {
        success: false,
        data: [],
        message: userMessage
      };
    }


  }
};
